import { useNavigate } from 'react-router-dom';

import type { MeterDetail } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Button } from '@fluxora/ui';

import { useRecordUsageProfileMutation } from '../api/usage-profile.queries';
import { useUsageSelection } from '../hooks/useUsageSelection';
import { buildShelfPath } from '../services/funnel-rehydrate.service';
import { declaredMonthlyKwhFor, homeProfileForMonthlyKwh } from '../services/usage.service';
import { useFunnel } from '../state/FunnelProvider';
import { ConsumptionSelector } from './ConsumptionSelector';
import { MeterSummaryCard } from './MeterSummaryCard';

/**
 * The resolved meter view, shared by BOTH on-ramps (SHOP-4). Composes:
 *   1. `MeterSummaryCard` — the confirmed service-point summary.
 *   2. `ConsumptionSelector` — pick monthly usage (auto-selected from the meter).
 *   3. the "Shop plans" CTA → the meter-filtered shelf (`/shop/plans`, SHOP-5A),
 *      DISABLED until a valid consumption is set (a tier, or a valid Custom kWh).
 *
 * The CTA reads validity from `FunnelProvider.monthlyUsageKwh` (the selector
 * writes it), so this component stays render-only.
 *
 * ── THE CTA CARRIES THE USAGE IN THE URL ───────────────────────────────────
 * The chosen monthly kWh + tier are the ONE piece of funnel state the BFF does not
 * hold — no funnel-session key stores them, because they are display state that only
 * ever fed the shelf's pricing. So they ride the query string
 * (`/shop/plans?usage=1200&tier=medium`), which means a refresh on the shelf keeps
 * the shopper's usage AND a priced shelf becomes bookmarkable/shareable. The path is
 * built by `buildShelfPath` — no string assembly in the `.tsx`.
 *
 * This CTA used to be the ONLY writer of those params, so the usage survived exactly
 * one hop. Every funnel link now carries it (`buildPlanDetailPath`/`buildPlanStepPath`),
 * and each reads the selection through the same `useUsageSelection` hook.
 *
 * ── AND IT ALSO TELLS THE SERVER (QA-2026-08-05 BLOCKER 1) ─────────────────
 * The URL is the right home for the DISPLAY usage, and the wrong home for the
 * number a regulated charge is derived from. The PUCT §25.478 deposit is sized
 * from the projected monthly bill, and the credit step that sizes it is reached
 * BEFORE the estimate screen — so if the shopper's home size only ever rode the
 * query string, the deposit engine never saw it and priced every customer as a
 * typical 1000 kWh home. Which it did: three personas, one identical deposit.
 *
 * So committing the consumption choice also RECORDS it on the funnel session,
 * once, here — the moment it stops being a radio button and becomes the basis for
 * everything downstream. `homeProfileForMonthlyKwh` owns the mapping; this
 * component only decides when to fire and when to leave.
 */
export interface ResolvedMeterPanelProps {
  readonly meterDetail: MeterDetail;
  /** Forwarded to the card's "start over" affordance (usually `clearResolved`). */
  readonly onStartOver?: () => void;
}

export function ResolvedMeterPanel({
  meterDetail,
  onStartOver,
}: ResolvedMeterPanelProps): JSX.Element {
  const { t } = useTranslation();
  const funnel = useFunnel();
  const navigate = useNavigate();

  const selection = useUsageSelection();
  const recordProfile = useRecordUsageProfileMutation();

  const canShop = funnel.monthlyUsageKwh !== undefined && funnel.monthlyUsageKwh > 0;
  const shelfPath = buildShelfPath(selection);

  const homeProfile =
    funnel.monthlyUsageKwh === undefined ? null : homeProfileForMonthlyKwh(funnel.monthlyUsageKwh);
  // E-SF19 P0a — the PRECISE figure, recorded next to the size. The size is a
  // three-member token and was the only thing that ever reached the server, so
  // the deposit engine and the estimate screen both priced a 120 kWh home at 500.
  // Both now read this value from the same funnel session, which is what keeps
  // "the deposit ceiling equals the estimated bill you were shown" true by
  // construction rather than by coincidence.
  const declaredMonthlyKwh = declaredMonthlyKwhFor(funnel.monthlyUsageKwh);

  /**
   * Record the home size, then go to the shelf — and go EITHER WAY.
   *
   * `onSettled`, not `onSuccess`: a failed preference write must not strand the
   * shopper on the meter screen. It is also not a correctness hazard, because the
   * estimate screen and the deposit read the SAME session — if the write never
   * lands, both fall back to the BFF's documented medium-home default together.
   * The shopper may be estimated as a typical home; they are never shown one bill
   * and sized off another.
   */
  const goToShelf = (): void => {
    if (homeProfile === null) {
      navigate(shelfPath);
      return;
    }
    recordProfile.mutate(
      {
        homeProfile,
        ...(declaredMonthlyKwh === null ? {} : { declaredMonthlyKwh }),
      },
      { onSettled: () => navigate(shelfPath) },
    );
  };

  return (
    <div className="anim-stagger flex flex-col gap-4">
      <MeterSummaryCard
        meterDetail={meterDetail}
        {...(onStartOver !== undefined ? { onStartOver } : {})}
      />

      <ConsumptionSelector estimatedAnnualUsageKwh={meterDetail.estimatedAnnualUsageKwh} />

      <div className="flex flex-col gap-1">
        <Button
          variant="primary"
          shape="pill"
          size="lg"
          className="cta-sheen"
          disabled={!canShop || recordProfile.isPending}
          onClick={goToShelf}
        >
          {t('shop.meter.summary.cta.shopPlans')}
        </Button>
        {!canShop ? (
          <span className="text-xs text-foreground/60">{t('shop.consumption.cta.hint')}</span>
        ) : null}
      </div>
    </div>
  );
}
